import { useRef, useState } from "react";
import { Formik, Form } from "formik";
import { Layers } from "lucide-react";
import {
  AppDialog,
  FormField,
  TextareaField,
  FileUploadDropzone,
} from "@/components/common";
import { Button } from "@/components/ui/button";
import { frameworkSchema } from "../schemas/custom-framework.schema";
import { resolveLogoUrl } from "../stores/custom-framework.store";

const LOGO_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/svg+xml";

// Caller must key this component by framework?._id (see CustomFrameworkListPage)
// so switching targets remounts it — the lazy useState initializer below
// only runs once per mount, it won't re-sync `logo` on a framework prop
// change without a remount.
export function FrameworkFormDialog({ open, onClose, onSubmit, framework }) {
  const formikRef = useRef();
  const isEdit = Boolean(framework?._id);
  const initialValues = {
    name: framework?.name ?? "",
    description: framework?.description ?? "",
  };

  const [logo, setLogo] = useState(() =>
    framework?.logoUrl ? { url: resolveLogoUrl(framework.logoUrl) } : null,
  );

  const [formikDirty, setFormikDirty] = useState(false);
  const isDirty = formikDirty || logo?.file != null;

  const handleClose = () => {
    onClose();
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => !v && handleClose()}
      isDirty={isDirty}
      title={isEdit ? "Edit Framework" : "New Custom Framework"}
      icon={Layers}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={() => formikRef.current?.submitForm()}>
            {isEdit ? "Save Changes" : "Create & Build"}
          </Button>
        </div>
      }
    >
      <Formik
        key={framework?._id ?? "new"}
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={frameworkSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await onSubmit(values, logo?.file ?? null);
            onClose();
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, dirty }) => {
          if (dirty !== formikDirty) setFormikDirty(dirty);
          return (
            <Form className="flex flex-col gap-4">
              <div className="flex items-start gap-4">
                <div className="w-40 shrink-0">
                  <FileUploadDropzone
                    label={null}
                    value={logo}
                    onChange={setLogo}
                    accept={LOGO_ACCEPT}
                    compact
                  />
                </div>
                <div className="flex-1">
                  <FormField
                    label="Framework Name"
                    name="name"
                    placeholder={
                      isEdit
                        ? "Framework name"
                        : "e.g. Internal Safety Framework"
                    }
                    required
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && errors.name}
                  />
                </div>
              </div>
              <TextareaField
                label="Description"
                name="description"
                placeholder="Optional description"
                rows={5}
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {isEdit && (
                <p className="text-xs text-muted-foreground">
                  Code: <span className="font-mono">{framework.code}</span>
                </p>
              )}
            </Form>
          );
        }}
      </Formik>
    </AppDialog>
  );
}
